import { useMemo, useState, type FormEvent } from "react";
import { gridForm, useSaveSpares, type GridBlock, type InspectionDetail, type Spare } from "../../api/inspections";
import { formatMoney } from "../../api/money";

/** What the grid starts out holding: every recorded spare's cells, named by
 * the fields the server told us to post them under. */
export function cellsFrom(detail: InspectionDetail): Record<string, string> {
  const cells: Record<string, string> = {};
  const put = (spare: Spare) => {
    cells[spare.fields.quantity] = String(spare.quantity);
    if (spare.free_issue) {
      cells[spare.fields.free_issue] = "on";
    }
    if (spare.replace) {
      cells[spare.fields.replace] = "on";
    }
    if (spare.catalogue_part_id === null) {
      cells[spare.fields.description] = spare.part_description;
      cells[spare.fields.manufacturer] = spare.manufacturer ?? "";
      cells[spare.fields.part_number] = spare.part_number ?? "";
      cells[spare.fields.notes] = spare.notes ?? "";
    }
  };
  detail.spares.rows.forEach(put);
  return cells;
}

/** Section 9: the spares this valve needs. One block of the catalogue at a
 * time (supplier and size), each part a row that fits a phone: the part,
 * then its quantity and the two ticks. A free-text row for what the
 * catalogue does not carry is folded away. Saved as one grid; prices show for
 * the office only. */
export default function SparesTab({ detail }: { detail: InspectionDetail }) {
  const save = useSaveSpares(detail.inspection.id);
  const canEdit = detail.actions.can_edit;
  const grid = detail.spares.grid;
  const suppliers = useMemo(() => Array.from(new Set(grid.map((g) => g.supplier))), [grid]);
  const recorded = detail.spares.rows.find((r) => r.supplier !== null);
  const [supplier, setSupplier] = useState(recorded?.supplier ?? suppliers[0] ?? "");
  const sizes = useMemo(() => grid.filter((g) => g.supplier === supplier).map((g) => g.size), [grid, supplier]);
  const [size, setSize] = useState(recorded?.size ?? sizes[0] ?? "");
  const block: GridBlock | undefined = grid.find((g) => g.supplier === supplier && g.size === (sizes.includes(size) ? size : sizes[0]));
  const [cells, setCells] = useState<Record<string, string>>(() => cellsFrom(detail));
  const set = (name: string, value: string) => setCells((c) => ({ ...c, [name]: value }));
  const toggle = (name: string, on: boolean) => set(name, on ? "on" : "");

  function submit(event: FormEvent) {
    event.preventDefault();
    if (detail.active_valve_id === null) {
      return;
    }
    save.mutate(
      { valve_id: detail.active_valve_id, form: gridForm(cells) },
      { onSuccess: (d) => setCells(cellsFrom(d)) },
    );
  }

  const newFields = detail.spares.new_fields;
  const office = detail.actions.office;
  const freeTextOpen = detail.spares.free_text.length > 0;

  function FreeRow({ f, fresh }: { f: Spare["fields"]; fresh: boolean }) {
    return (
      <div className="spare-free">
        <label>
          {fresh ? "New part" : "Part"}
          <input value={cells[f.description] ?? ""} onChange={(e) => set(f.description, e.target.value)} placeholder={fresh ? "Describe the part" : undefined} readOnly={!canEdit} />
        </label>
        <label>
          Manufacturer
          <input value={cells[f.manufacturer] ?? ""} onChange={(e) => set(f.manufacturer, e.target.value)} readOnly={!canEdit} />
        </label>
        <label>
          Part no.
          <input value={cells[f.part_number] ?? ""} onChange={(e) => set(f.part_number, e.target.value)} readOnly={!canEdit} />
        </label>
        <label>
          Qty
          <input className="num" inputMode="numeric" value={cells[f.quantity] ?? ""} onChange={(e) => set(f.quantity, e.target.value)} readOnly={!canEdit} />
        </label>
        <label>
          <input type="checkbox" checked={cells[f.replace] === "on"} onChange={(e) => toggle(f.replace, e.target.checked)} disabled={!canEdit} /> Replace
        </label>
        <label>
          Notes
          <input value={cells[f.notes] ?? ""} onChange={(e) => set(f.notes, e.target.value)} readOnly={!canEdit} />
        </label>
      </div>
    );
  }

  return (
    <div className="insp-section">
      <div className="insp-section-head">
        <h3>9 · Spares needed</h3>
        <span className="insp-section-sub">What this valve needs; the office quotes and orders from it</span>
      </div>

      <form onSubmit={submit}>
        {grid.length === 0 ? (
          <p className="insp-hint">The spares catalogue has not been imported; write the parts in below.</p>
        ) : (
          <>
            <div className="insp-row-fields">
              <label>
                Supplier
                <select
                  value={supplier}
                  onChange={(e) => {
                    setSupplier(e.target.value);
                    setSize(grid.find((g) => g.supplier === e.target.value)?.size ?? "");
                  }}
                >
                  {suppliers.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Size
                <select value={sizes.includes(size) ? size : sizes[0] ?? ""} onChange={(e) => setSize(e.target.value)}>
                  {sizes.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              {detail.spares.manufacturer_default ? <span className="field-hint">Section 3 says {detail.spares.manufacturer_default}</span> : null}
            </div>
            {block ? (
              <div className="spare-rows" role="list" aria-label={`Parts from ${block.supplier} ${block.size}`}>
                {block.parts.map((part) => {
                  const qty = cells[part.fields.quantity] ?? "";
                  return (
                    <div key={part.id} className={`spare-row${part.active ? "" : " warn"}${qty !== "" ? " chosen" : ""}`} role="listitem">
                      <div className="spare-part">
                        <span className="spare-name">
                          {part.description}
                          {part.active ? "" : " (withdrawn)"}
                        </span>
                        <span className="spare-meta mono">
                          {part.item_code ?? ""}
                          {office && part.selling_price ? ` · ${formatMoney(part.selling_price)}` : ""}
                        </span>
                      </div>
                      <label className="spare-qty">
                        Qty
                        <input
                          className="num"
                          inputMode="numeric"
                          aria-label={`Quantity of ${part.description}`}
                          value={qty}
                          onChange={(e) => set(part.fields.quantity, e.target.value)}
                          readOnly={!canEdit}
                        />
                      </label>
                      <label className="spare-tick">
                        <input type="checkbox" checked={cells[part.fields.free_issue] === "on"} onChange={(e) => toggle(part.fields.free_issue, e.target.checked)} disabled={!canEdit} />
                        Free issue
                      </label>
                      <label className="spare-tick">
                        <input type="checkbox" checked={cells[part.fields.replace] === "on"} onChange={(e) => toggle(part.fields.replace, e.target.checked)} disabled={!canEdit} />
                        Replace
                      </label>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </>
        )}

        <details className="insp-more" open={freeTextOpen || grid.length === 0}>
          <summary>A part that is not in the list</summary>
          {detail.spares.free_text.map((s) => (
            <FreeRow key={s.id} f={s.fields} fresh={false} />
          ))}
          {canEdit ? <FreeRow f={newFields} fresh /> : null}
        </details>

        {canEdit ? (
          <div className="insp-save-row">
            <button type="submit" className="btn btn-primary" disabled={save.isPending || detail.active_valve_id === null}>
              {save.isPending ? "Saving…" : "Save spares"}
            </button>
            {save.isSuccess ? <span className="insp-save ok">Spares saved.</span> : null}
          </div>
        ) : null}
        {save.isError ? <p className="notice bad">{save.error.message}</p> : null}
      </form>

      {detail.spares.rows.length > 0 ? (
        <section className="insp-subsection">
          <h4>Recorded for this valve</h4>
          <ul className="insp-plain-list">
            {detail.spares.rows.map((s) => (
              <li key={s.id}>
                {s.quantity} × {s.part_description}
                {s.manufacturer ? ` · ${s.manufacturer}` : ""}
                {s.part_number ? ` · ${s.part_number}` : ""}
                {s.supplier ? ` · ${s.supplier} ${s.size ?? ""}` : " · not in the list"}
                {s.free_issue ? " · free issue" : ""}
                {s.replace ? " · replace" : ""}
                {s.notes ? ` · ${s.notes}` : ""}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {office ? (
        <section className="insp-subsection">
          <h4>Quote and order the parts</h4>
          {detail.quotable_suppliers.length > 0 ? (
            <p>
              Quotable from {detail.quotable_suppliers.map((s) => s.name).join(", ")}.{" "}
              <a className="btn" href="/quotations">
                Quotations
              </a>
            </p>
          ) : (
            <p className="insp-hint">No catalogue part with a supplier account behind it yet.</p>
          )}
          {detail.unquotable.length > 0 ? (
            <ul className="insp-plain-list">
              {detail.unquotable.map((u) => (
                <li key={u}>{u}</li>
              ))}
            </ul>
          ) : null}
          {detail.quotations.length > 0 ? (
            <ul className="insp-plain-list">
              {detail.quotations.map((q) => (
                <li key={q.id}>
                  <a href={`/quotations/${q.id}`}>{q.number ?? `Quotation ${q.id}`}</a> · {q.status}
                </li>
              ))}
            </ul>
          ) : null}
          {detail.purchase_orders.length > 0 ? (
            <ul className="insp-plain-list">
              {detail.purchase_orders.map((o) => (
                <li key={o.id}>
                  <a href={`/purchase-orders/${o.id}`}>{o.number}</a> · {o.status}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
